/**
 * Subscription Constants
 * Contains pricing data, features, and configuration for all subscription plans
 */

import { SubscriptionPlan, SubscriptionPlanDetails } from '~types/subscription/subscription.types';

export const SUBSCRIPTION_PLANS: Record<SubscriptionPlan, SubscriptionPlanDetails> = {
  [SubscriptionPlan.RESET_TABLE]: {
    id: SubscriptionPlan.RESET_TABLE,
    name: '✨ RESET TABLE UNLIMITED',
    shortName: 'Reset Table',
    description: 'Unlimited dinners with priority matching',
    pricing: {
      monthly: 14.99,
      currency: '£',
    },
    color: '#FF9500',
    highlighted: false,
    features: [
      {
        id: 'unlimited_dinners',
        name: 'Unlimited dinners',
        description: 'Enjoy unlimited dinner events',
        included: true,
      },
      {
        id: 'reduced_no_show_fee',
        name: 'Reduced no-show fee',
        description: '£3 vs £6',
        included: true,
      },
      {
        id: 'last_minute_join',
        name: 'Join a dinner last minute',
        description: 'Up to 2 hours before the event',
        included: true,
      },
      {
        id: 'plus_one_guest',
        name: 'Bring a +1 for only £2',
        description: 'Invite a friend to any dinner',
        included: true,
      },
      {
        id: 'best_match_event',
        name: 'Free event with your 5 best matches',
        description: 'At least 2 event attendance required',
        included: true,
      },
      {
        id: 'cancel_anytime',
        name: 'Cancel anytime',
        description: 'No long-term commitment',
        included: true,
      },
    ],
  },
  [SubscriptionPlan.CULTURE_EDIT]: {
    id: SubscriptionPlan.CULTURE_EDIT,
    name: '🎭 CULTURAL EDIT + RESET TABLE UNLIMITED',
    shortName: 'Cultural Edit',
    description: 'Unlimited dinners + culture events',
    pricing: {
      monthly: 21.99,
      currency: '£',
    },
    color: '#FF9500',
    highlighted: false,
    features: [
      {
        id: 'unlimited_dinners',
        name: 'Unlimited dinners',
        description: 'Enjoy unlimited dinner events',
        included: true,
      },
      {
        id: 'culture_edit',
        name: 'Unlimited Cultural Events',
        description: 'Access unlimited cultural events',
        included: true,
      },
      {
        id: 'no_show_fee_waiver',
        name: 'No-show fee waiver',
        description: 'Waived no-show fee for all events',
        included: true,
      },
      {
        id: 'last_minute_join',
        name: 'Join a dinner last minute',
        description: 'Up to 2 hours before the event',
        included: true,
      },
      {
        id: 'plus_one_guest_free',
        name: 'Bring a +1 for FREE',
        description: 'Invite a friend for free to any dinner',
        included: true,
      },
      {
        id: 'best_match_event',
        name: 'Free event with your 5 best matches',
        description: 'At least 2 event attendance required',
        included: true,
      },
      {
        id: 'cancel_anytime',
        name: 'Cancel anytime',
        description: 'No long-term commitment',
        included: true,
      },
    ],
  },
  [SubscriptionPlan.ALL_ACCESS]: {
    id: SubscriptionPlan.ALL_ACCESS,
    name: '🎉 ALL ACCESS + MONTHLY HOUSEWARMING PARTY',
    shortName: 'All Access',
    description: 'Access to all experiences including our monthly signature party',
    pricing: {
      monthly: 32.99,
      currency: '£',
    },
    color: '#FF9500',
    highlighted: true,
   // badge: 'Most Popular',
    features: [
      {
        id: 'unlimited_dinners',
        name: 'Unlimited dinners',
        description: 'Enjoy unlimited dinner events',
        included: true,
      },
      {
        id: 'culture_edit',
        name: 'Unlimited Cultural Events',
        description: 'Access unlimited cultural events',
        included: true,
      },
      {
        id: 'housewarming_party',
        name: 'Pyxi Housewarming Party',
        description: 'Exclusive monthly housewarming party access',
        included: true,
      },
      {
        id: 'no_show_fee_waiver',
        name: 'No-show fee waiver',
        description: 'Waived no-show fee for all events',
        included: true,
      },
      {
        id: 'last_minute_join',
        name: 'Join a dinner last minute',
        description: 'Up to 2 hours before the event',
        included: true,
      },
      {
        id: 'plus_one_guest_free',
        name: 'Bring a +1 for FREE',
        description: 'Invite a friend for free to any dinner',
        included: true,
      },
      {
        id: 'best_match_event',
        name: 'Free event with your 5 best matches',
        description: 'At least 2 event attendance required',
        included: true,
      },
      {
        id: 'cancel_anytime',
        name: 'Cancel anytime',
        description: 'No long-term commitment',
        included: true,
      },
    ],
  },
};

export const SUBSCRIPTION_PLAN_ORDER = [
  SubscriptionPlan.RESET_TABLE,
  SubscriptionPlan.CULTURE_EDIT,
  SubscriptionPlan.ALL_ACCESS,
];

/**
 * Get plan details by ID
 */
export const getPlanDetails = (planId: SubscriptionPlan): SubscriptionPlanDetails => {
  return SUBSCRIPTION_PLANS[planId];
};

/**
 * Get all subscription plans in order
 */
export const getAllSubscriptionPlans = (): SubscriptionPlanDetails[] => {
  return SUBSCRIPTION_PLAN_ORDER.map(planId => SUBSCRIPTION_PLANS[planId]);
};
